import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";

const PrivacyPolicy = () => {
  return (
    <>
      <SEO
        title="Privacy Policy | TPS Housing"
        description="TPS Housing privacy policy. Learn how we collect, use, and protect your personal information."
        canonicalUrl="/privacy-policy" />

      <div className="min-h-screen">
        <Navigation />
        <main className="pt-24 pb-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <h1 className="text-4xl font-bold text-foreground mb-2">United States Privacy Policy and GLBA Statement</h1>
            <p className="text-muted-foreground mb-10">Last Modified: September 15, 2026</p>

            <div className="prose prose-lg max-w-none space-y-8 text-foreground/90">

              {/* Introduction */}
              <section>
                <p>
                  In TPS Housing's business, the USA privacy policy is comprised of two documents which are to be read together:
                </p>
                <ol className="list-decimal pl-6 space-y-1">
                  <li>This main policy document.</li>
                  <li>The GLBA Statement below: a supplementary document focused primarily on consumer financial privacy protections offered by the US federal statute GLBA (The Gramm-Leach-Bliley Act).</li>
                </ol>
              </section>

              {/* FACTS Section */}
              <section className="bg-muted/30 rounded-xl p-6 border border-border">
                <h2 className="text-2xl font-semibold text-foreground">Facts</h2>
                <p className="text-lg font-medium text-primary">What does TPS Housing do with your personal information?</p>
              </section>

              {/* WHY */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">Why?</h2>
                <p>
                  Companies that provide housing and financial-related services choose how they share your personal information. Federal law gives consumers the right to limit some but not all sharing. Federal law also requires us to tell you how we collect, share, and protect your personal information. Please read this notice carefully to understand what we do.
                </p>
              </section>

              {/* WHAT */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">What?</h2>
                <p>The types of personal information we collect and share depend on the product or service you have with us. This information can include, without limitation:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Government identification numbers, such as: driver's license, state identification, passport, visa or Social Security number</li>
                  <li>Name, address, email address, mobile and home telephone number</li>
                  <li>Insurance claim details, policy information, and claim history</li>
                  <li>Housing preferences, family dynamics, and placement history</li>
                  <li>Payment and billing information, including credit and payment history</li>
                  <li>Computer and mobile device information such as the domain and host you use to access the Internet; your computer's IP address; mobile device geolocation, number and other information from and about your device (such as device properties, settings, applications, stored information and usage) and carrier; the browser and operating system software you use; social profile and network information; the date and time you access our website and the Internet address used to link to our website when you visit us</li>
                  <li>Data cookies; see <a href="https://www.cookiesandyou.com/" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">cookiesandyou.com</a> to learn what cookies are and how to disable them</li>
                </ul>
                <p className="mt-4">
                  You consent to our collection, transfer and storage of your personal information by computers or other transfer or storage devices in the United States and elsewhere.
                </p>
              </section>

              {/* HOW - Sharing Table */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">How We Share Your Information</h2>
                <p>In the section below, we list the reasons companies generally can share their customers' and consumers' personal information, the reasons TPS Housing chooses to share, and whether you can limit this sharing.</p>

                <div className="overflow-x-auto mt-4">
                  <table className="w-full border-collapse border border-border text-sm">
                    <thead>
                      <tr className="bg-muted">
                        <th className="border border-border p-3 text-left font-semibold">Reasons we can share your personal information</th>
                        <th className="border border-border p-3 text-left font-semibold">Does TPS Housing share?</th>
                        <th className="border border-border p-3 text-left font-semibold">Can you limit this sharing?</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-border p-3">For our everyday business purposes — such as to process your transactions; maintain your account(s); facilitate housing placements; respond to court orders and legal investigations; report to credit bureaus; gather information about website access, usage and performance; enhance your consumer experience; validate your identity; or as otherwise permitted or required by law</td>
                        <td className="border border-border p-3">Yes</td>
                        <td className="border border-border p-3">No</td>
                      </tr>
                      <tr className="bg-muted/30">
                        <td className="border border-border p-3">For our marketing purposes — such as offering to you our products and services or notifying you about future events and promotions</td>
                        <td className="border border-border p-3">Yes</td>
                        <td className="border border-border p-3">No</td>
                      </tr>
                      <tr>
                        <td className="border border-border p-3">For joint marketing with other companies</td>
                        <td className="border border-border p-3">No</td>
                        <td className="border border-border p-3">We don't share</td>
                      </tr>
                      <tr className="bg-muted/30">
                        <td className="border border-border p-3">For our affiliates' everyday business purposes — such as information about your transactions and experiences</td>
                        <td className="border border-border p-3">Yes</td>
                        <td className="border border-border p-3">No</td>
                      </tr>
                      <tr>
                        <td className="border border-border p-3">For our affiliates' everyday business purposes — such as information about your creditworthiness</td>
                        <td className="border border-border p-3">Yes</td>
                        <td className="border border-border p-3">Yes</td>
                      </tr>
                      <tr className="bg-muted/30">
                        <td className="border border-border p-3">For our affiliates to market to you</td>
                        <td className="border border-border p-3">Yes</td>
                        <td className="border border-border p-3">Yes</td>
                      </tr>
                      <tr>
                        <td className="border border-border p-3">For nonaffiliates to market to you</td>
                        <td className="border border-border p-3">No</td>
                        <td className="border border-border p-3">We don't share</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* To Limit Our Sharing */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">To Limit Our Sharing</h2>
                <h3 className="text-xl font-medium text-foreground mt-4">Opt-Out Right (Except for Vermont and California Consumers)</h3>
                <p>
                  If you prefer that we limit sharing with affiliates or nonaffiliated third parties as described above, you may opt-out of those disclosures as follows:
                </p>
                <ul className="list-none space-y-2 mt-2">
                  <li><strong>Email:</strong> <a href="mailto:info@tpshousing.com" className="text-primary hover:underline">info@tpshousing.com</a></li>
                  <li><strong>Mail to:</strong> TPS Housing Privacy Officer, 10810 N. Tatum Blvd. #102-222, Phoenix, AZ 85028</li>
                </ul>

                <h3 className="text-xl font-medium text-foreground mt-6">Vermont and California Consumers</h3>
                <p>
                  TPS Housing will disclose information about consumers with a Vermont or California mailing address only with your written authorization ("consent"), unless otherwise permitted or required by law. Written authorizations from Vermont consumers may be delivered to us at the address above or by email at{" "}
                  <a href="mailto:info@tpshousing.com" className="text-primary hover:underline">info@tpshousing.com</a>{" "}
                  and must contain your name, address, signature and your consent. You may revoke your consent at any time by writing or emailing us at the addresses provided above. California consumers should contact us at the addresses above for instructions on how to deliver their consent.
                </p>

                <div className="bg-muted/30 rounded-lg p-4 mt-4 border border-border">
                  <p className="text-sm">
                    <strong>Note:</strong> Disclosures not subject to an opt-out choice include: disclosures necessary to effect, administer or enforce a transaction you request; disclosures to our authorized service providers; disclosures permitted or required by law or disclosures to prevent fraud or other illegal activities.
                  </p>
                  <p className="text-sm mt-2">
                    <strong>Please note:</strong> If you are a new consumer or customer, we can begin sharing your information 30 days from the date we provided this notice. When you are no longer our consumer or customer, we continue to share your information as described in this notice. However, you can contact us at any time to limit our sharing.
                  </p>
                </div>
              </section>

              {/* What We Do */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">What We Do</h2>

                <h3 className="text-xl font-medium text-foreground mt-4">How does TPS Housing protect my personal information?</h3>
                <p>
                  To help protect your personal information from unauthorized access, use and disclosure, we use reasonable security measures. These measures include physical, electronic and procedural safeguards such as computer safeguards and secured files and buildings. As a SOC 2 Type 2 compliant organization, we maintain rigorous security protocols. We also take measures to limit personal information access to only employees, agents and representatives that need to know. Despite our efforts, third parties may unlawfully intercept or access your transmissions to us or may wrongly instruct you to disclose information to them by posing as TPS Housing or by misinforming you about our services. Always use caution and good judgment when using Internet or mobile phone technologies.
                </p>

                <h3 className="text-xl font-medium text-foreground mt-4">How does TPS Housing collect my personal information?</h3>
                <p>We collect your personal information, for example, when you:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Submit a claim or request temporary housing placement</li>
                  <li>Complete an application or provide information on forms</li>
                  <li>Use our website or portal (for example, to manage your account, submit property listings, or provide your preferences)</li>
                  <li>Communicate with us via email, phone, or our messaging system</li>
                </ul>
              </section>

              {/* Why Can't I Limit All Sharing */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">Why can't I limit all sharing?</h2>
                <p>Federal law gives you the right to limit only:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Sharing for affiliates' everyday business purposes — information about your creditworthiness</li>
                  <li>Affiliates from using your information to market to you</li>
                  <li>Sharing for nonaffiliates to market to you</li>
                </ul>
                <p className="mt-2">State laws and individual companies may give you additional rights to limit sharing. For details, see the "To limit our sharing" section above.</p>

                <h3 className="text-xl font-medium text-foreground mt-4">What happens when I limit sharing for an account I hold jointly with someone else?</h3>
                <p>Your choices will apply to everyone on your account.</p>
              </section>

              {/* Definitions */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">Definitions</h2>

                <h3 className="text-xl font-medium text-foreground mt-4">Affiliates</h3>
                <p>Companies related by common ownership or control. They can be financial and nonfinancial companies. 

                </p>

                <h3 className="text-xl font-medium text-foreground mt-4">Nonaffiliates</h3>
                <p>
                  Companies not related by common ownership or control. They can be financial and nonfinancial companies. For example, we may disclose information about current and former consumers and customers to third parties, including without limitation: insurance carriers and adjusters to facilitate claims; property owners and landlords to arrange housing; service providers that we contract with to operate our platform; and otherwise as permitted or required by law.
                </p>

                <h3 className="text-xl font-medium text-foreground mt-4">Joint Marketing</h3>
                <p>A formal agreement between nonaffiliated companies that together market products or services to you.</p>
              </section>

              {/* California Consumers */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">California Consumers</h2>
                <p>
                  Effective January 1, 2020, the California Consumer Privacy Act ("CCPA") permits consumers who are California residents to (a) ask a covered business which categories or pieces of personal information it collects and how the information is used; (b) request deletion of the information; and (c) opt out of the sale of such information, if applicable. The CCPA sets forth certain exceptions to these general provisions. To contact us with questions about our compliance with the CCPA, email us at{" "}
                  <a href="mailto:info@tpshousing.com" className="text-primary hover:underline">info@tpshousing.com</a>.
                  {" "}Please note, consumers will not receive discriminatory treatment for the exercise of privacy rights conferred by the CCPA.
                </p>
              </section>

              {/* Texas Consumers */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">Texas Consumers</h2>
                <p>
                  If you have a complaint, first email{" "}
                  <a href="mailto:info@tpshousing.com" className="text-primary hover:underline">info@tpshousing.com</a>.
                  {" "}If you still have an unresolved complaint, please direct your complaint to: Texas Department of Banking, 2601 North Lamar Boulevard, Austin, Texas, 78705, 1-877-276-5554 (toll free){" "}
                  <a href="http://www.dob.texas.gov/" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">www.dob.texas.gov</a>.
                </p>
              </section>

              {/* Children's Privacy */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">Children's Privacy</h2>
                <p>
                  Our website is not directed at children under the age of 13. TPS Housing does not knowingly collect or maintain information at our website from persons under the age of 13.
                </p>
              </section>

              {/* External Websites */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">External Websites</h2>
                <p>
                  Our website may be linked to or from third party websites. TPS Housing is not responsible for the content or privacy practices of websites that are linked to or from our website.
                </p>
              </section>

              {/* Changes */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">Changes to This Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on this page with a revised effective date.
                </p>
              </section>

              {/* Questions / Contact */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground">Questions?</h2>
                <p>If you have questions about this Privacy Policy, please contact us:</p>
                <ul className="list-none space-y-1 mt-2">
                  <li><strong>Email:</strong> <a href="mailto:info@tpshousing.com" className="text-primary hover:underline">info@tpshousing.com</a></li>
                  <li><strong>Phone:</strong> <a href="tel:760-687-1001" className="text-primary hover:underline">760-687-1001</a></li>
                  <li><strong>Address:</strong> 10810 N. Tatum Blvd. #102-222, Phoenix, AZ 85028</li>
                </ul>
              </section>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>);

};

export default PrivacyPolicy;